/**
 * Hand simulator for AI look-ahead. It replays the engine's turn rules on a *sampled* world
 * (a guess at the hidden cards), never on the live game, so nothing here can touch the real deck,
 * hands or scores.
 *
 * Rules mirrored from `whiskey.ts` (a test plays both side by side to keep them in step):
 *   seat order, free REVEAL then act again, TAKE_MIDDLE swaps whole hands and turns the middle up (never on the turn the seat turned it over),
 *   SWAP_ONE, BLIND (discard chosen before the draw; empty bones → shuffle the discards), KNOCK →
 *   the other four players get one last turn, 5 passes in a row end the hand, 80-action cap.
 * Showdown uses the engine's own `evaluate` / `compareHands` and `scoreHand`.
 */
import type { Card } from "../cards.ts";
import { evaluate, compareHands, type HandRank } from "../handEval.ts";
import { positionPoints } from "../scoring.ts";
import { type Action, legalize, mustReveal } from "../whiskey.ts";
import type { Rng } from "../rng.ts";

export const MAX_ACTIONS_PER_HAND = 80;

export interface SimState {
  hands: Card[][];
  middle: Card[];
  middleRevealed: boolean;
  /** The seat to move turned the middle over this turn (may not take all five until its next turn). */
  justRevealed?: boolean;
  stock: Card[];          // top of the bones = last element (engine pops)
  discard: Card[];
  seat: number;           // whose turn it is
  dealerSeat?: number;    // may not take the middle blind
  knockerSeat: number;
  finalTurnsLeft: number;
  turnsTaken: number[];
  consecutiveKeeps: number;
  actions: number;
  done: boolean;
}

export function cloneSim(s: SimState): SimState {
  return {
    hands: s.hands.map((h) => h.slice()),
    middle: s.middle.slice(),
    middleRevealed: s.middleRevealed,
    justRevealed: s.justRevealed,
    stock: s.stock.slice(),
    discard: s.discard.slice(),
    seat: s.seat,
    dealerSeat: s.dealerSeat,
    knockerSeat: s.knockerSeat,
    finalTurnsLeft: s.finalTurnsLeft,
    turnsTaken: s.turnsTaken.slice(),
    consecutiveKeeps: s.consecutiveKeeps,
    actions: s.actions,
    done: s.done,
  };
}

/** Apply one card action for the seat to move (REVEAL is handled by the caller as a free action). */
export function applyAction(s: SimState, a: Action, rng: Rng): void {
  const seat = s.seat;
  switch (a.type) {
    case "KEEP":
    case "KNOCK":
    case "REVEAL":
      break;
    case "TAKE_MIDDLE": {
      const old = s.hands[seat];
      s.hands[seat] = s.middle.slice();
      s.middle = old.slice();
      s.middleRevealed = true;
      break;
    }
    case "SWAP_ONE": {
      const gi = clamp(a.give), ti = clamp(a.take);
      const taken = s.middle[ti];
      s.middle[ti] = s.hands[seat][gi];
      s.hands[seat][gi] = taken;
      break;
    }
    case "BLIND": {
      if (s.stock.length === 0 && s.discard.length > 0) s.stock = rng.shuffle(s.discard.splice(0));
      if (s.stock.length === 0) break;
      const drawn = s.stock.pop()!;
      const gi = clamp(a.give);
      s.discard.push(s.hands[seat][gi]);
      s.hands[seat][gi] = drawn;
      break;
    }
  }
}

/** Book-keeping after a seat's turn (and its optional knock), then advance to the next seat. */
export function endTurn(s: SimState, a: Action, knockedAfter: boolean): void {
  const seat = s.seat;
  s.justRevealed = false;
  s.turnsTaken[seat]++;
  if ((a.type === "KNOCK" || knockedAfter) && s.knockerSeat === -1) {
    s.knockerSeat = seat;
    s.finalTurnsLeft = 4;
  }
  s.consecutiveKeeps = a.type === "KEEP" && !knockedAfter ? s.consecutiveKeeps + 1 : 0;
  s.actions++;
  if (s.knockerSeat !== -1) {
    if (seat !== s.knockerSeat) s.finalTurnsLeft--;
    if (s.finalTurnsLeft <= 0) { s.done = true; return; }
  }
  if (s.consecutiveKeeps >= 5 || s.actions >= MAX_ACTIONS_PER_HAND) { s.done = true; return; }
  s.seat = (s.seat + 1) % 5;
  if (s.knockerSeat !== -1 && s.seat === s.knockerSeat) {
    // the knocker is skipped; the engine still counts it as an action
    s.actions++;
    if (s.actions >= MAX_ACTIONS_PER_HAND) { s.done = true; return; }
    s.seat = (s.seat + 1) % 5;
  }
}

/** A seat's policy inside a simulation. It may only look at its own hand and the visible middle. */
export type RolloutPolicy = (s: SimState, seat: number, rng: Rng) => { action: Action; knockAfter: boolean };

/** Play the hand out from the current state. `horizon` caps the number of further turns. */
export function playOut(s: SimState, policies: RolloutPolicy[], rng: Rng, horizon = 40): void {
  let turns = 0;
  while (!s.done && turns < horizon) {
    const seat = s.seat;
    let { action, knockAfter } = policies[seat](s, seat, rng);
    if (mustReveal(action, s.middleRevealed, seat === s.dealerSeat)) { s.middleRevealed = true; s.justRevealed = true; ({ action, knockAfter } = policies[seat](s, seat, rng)); }
    action = legalize(action, { revealed: s.middleRevealed, knocked: s.knockerSeat !== -1, dealer: seat === s.dealerSeat, justRevealed: s.justRevealed });
    applyAction(s, action, rng);
    const exchanged = action.type === "SWAP_ONE" || action.type === "TAKE_MIDDLE" || action.type === "BLIND";
    endTurn(s, action, knockAfter && exchanged && s.knockerSeat === -1 && s.middleRevealed);
    turns++;
  }
}

export interface Showdown {
  ranks: HandRank[];
  /** Seats best → worst (never tied: the evaluator breaks ties on suits). */
  order: number[];
  /** Hand points for each seat, before the contig bonus. */
  points: number[];
}

export function showdown(hands: Card[][]): Showdown {
  const ranks = hands.map((h) => evaluate(h));
  const order = [0, 1, 2, 3, 4].sort((a, b) => compareHands(ranks[b], ranks[a]));
  const points = [0, 0, 0, 0, 0];
  order.forEach((seat, i) => (points[seat] = positionPoints(i + 1) + ranks[seat].category));
  return { ranks, order, points };
}

function clamp(n: number): number {
  return Math.max(0, Math.min(4, n | 0));
}
